/**
 * Le contrat de la console, vu du frontend — types et hooks TanStack Query.
 *
 * Tout part de `/api/console/*` et emprunte le client HTTP commun (`lib/api`),
 * donc le cookie de session et le double-submit CSRF sont gérés comme partout
 * ailleurs. Rien de particulier n'est réinventé ici pour la console : les seuls
 * écarts sont ceux que la console impose vraiment, et ils sont commentés.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'

import { ApiError, api } from '../../lib/api'

/* -------------------------------------------------------------------------- */
/* Vocabulaire                                                                */
/* -------------------------------------------------------------------------- */

export type StatutTenant = 'provisionnement' | 'actif' | 'suspendu' | 'expire'
/**
 * ★ `evenement` a rejoint la liste : le produit vend deux offres (une salle à
 *   programmation, une soirée ponctuelle) et la console n'en provisionnait
 *   qu'une. Voir `plateforme/console/dto.ts`.
 */
export type TypeTenant = 'demo' | 'client' | 'evenement'

/**
 * Les QUATRE états de santé. Jamais réduits à un booléen, jamais fusionnés en
 * « vert / rouge » : « en retard » se répare en repassant un déploiement,
 * « injoignable » en rallumant un serveur. Deux causes, deux remèdes, deux
 * couleurs.
 */
export type StatutSante = 'a_jour' | 'en_retard' | 'en_echec' | 'injoignable'

export interface LigneTenant {
  readonly id: string
  readonly slug: string
  readonly nom: string
  readonly type: TypeTenant
  readonly statut: StatutTenant
  readonly creeLe: string
  readonly expireLe: string | null
  readonly baseSupprimeeLe: string | null
  readonly versionMigration?: string
  readonly versionAttendue?: string
  readonly sante: StatutSante
  readonly joursAvantExpiration: number | null
}

export interface Volumetrie {
  readonly abonnes: number
  readonly billets: number
  readonly representations: number
  readonly souscriptions: number
  readonly sieges: number
  readonly jaugePrincipale: number
}

export interface Quota {
  readonly mesure: number
  readonly repere: number
  readonly niveau: 'ok' | 'proche' | 'depasse'
}

export interface EntreeJournalPlateforme {
  readonly id: string
  readonly action: string
  readonly tenantSlug: string | null
  readonly auteurEmail: string | null
  readonly details: Record<string, unknown> | null
  readonly createdAt: string
}

export interface FicheTenant {
  readonly tenant: {
    readonly id: string
    readonly slug: string
    readonly nom: string
    readonly domainePublic?: string | null
    readonly type: TypeTenant
    readonly statut: StatutTenant
    readonly creeLe: string
    readonly expireLe: string | null
    readonly notes: string | null
    readonly activeLe: string | null
    /** ★ LOT B-4 — dernière étape atteinte avant un provisionnement interrompu. */
    readonly etapeCourante: string | null
    readonly suspenduLe: string | null
    readonly motifSuspension: string | null
    readonly suppressionPrevueLe: string | null
    readonly baseSupprimeeLe: string | null
    readonly baseNom: string
  }
  readonly sante: {
    readonly statut: StatutSante
    readonly derniereVersion?: string
    readonly versionAttendue?: string
    readonly versionsManquantes: readonly string[]
    readonly versionsEnEchec: readonly string[]
    readonly derniereErreur?: string
  }
  readonly volumetrie?: Volumetrie
  readonly volumetrieIndisponible?: string
  readonly palier?: { readonly code: string; readonly forfaitAnnuel: number | null }
  readonly quotas?: { readonly sieges: Quota; readonly abonnes: Quota }
  readonly journal: readonly EntreeJournalPlateforme[]
  readonly bascules: readonly {
    readonly id: string
    readonly auteurEmail: string | null
    readonly motif: string
    readonly ouverteLe: string
    readonly fermeeLe: string | null
    readonly causeFin: string | null
  }[]
  readonly joursAvantExpiration: number | null
  readonly delaiSuppressionJours: number
}

export interface EcranSante {
  readonly versionAttendue?: string
  readonly parStatut: Record<StatutSante, number>
  readonly tenants: readonly {
    readonly slug: string
    readonly nom: string
    readonly statutTenant: StatutTenant
    readonly statut: StatutSante
    readonly derniereVersion?: string
    readonly versionsManquantes: readonly string[]
    readonly versionsEnEchec: readonly string[]
    readonly derniereErreur?: string
  }[]
}

export interface Statistiques {
  readonly parStatut: Record<StatutTenant, number>
  readonly parSante: Record<StatutSante, number>
  readonly basculesOuvertes: number
  readonly total: Volumetrie
  readonly injoignables: readonly string[]
  readonly parTenant: readonly {
    readonly slug: string
    readonly nom: string
    readonly type: TypeTenant
    readonly joignable: boolean
    readonly volumetrie?: Volumetrie
    readonly palier?: string
  }[]
  readonly expirentBientot: readonly {
    readonly slug: string
    readonly nom: string
    readonly expireLe: string
    readonly joursRestants: number
  }[]
}

export interface Bascule {
  readonly id: string
  readonly tenantId: string
  readonly tenantSlug: string
  readonly tenantNom: string
  readonly motif: string
  readonly ouverteLe: string
  readonly expireLe: string
  readonly lectureSeule: true
}

export interface SessionConsole {
  readonly superadmin: { readonly id: string; readonly email: string; readonly nom: string }
  readonly bascule: Bascule | null
}

export interface ApercuTheatre {
  readonly identite: {
    readonly nom: string
    readonly emailExpediteur: string
    readonly logo: boolean
  } | null
  readonly salles: readonly {
    readonly nom: string
    readonly active: boolean
    readonly jauge: number
  }[]
  readonly saisons: readonly {
    readonly libelle: string
    readonly phase: string
    readonly spectacles: number
  }[]
  readonly comptes: readonly {
    readonly email: string
    readonly nom: string
    readonly role: string
    readonly actif: boolean
  }[]
  readonly impayes: { readonly souscriptions: number; readonly montant: number }
}

export interface EntreeJournalTheatre {
  readonly id: string
  readonly journal: string
  readonly entite: string
  readonly entiteId: string | null
  readonly action: string
  readonly acteur: string
  readonly details: Record<string, unknown> | null
  readonly createdAt: string
}

export interface ResultatRecherche {
  readonly id: string
  readonly nom: string
  readonly prenom: string
  readonly email: string | null
  readonly localite: string | null
}

export interface DossierPersonne {
  readonly personne: {
    readonly id: string
    readonly nom: string
    readonly prenom: string
    readonly email: string | null
    readonly telephone: string | null
    readonly localite: string | null
  }
  readonly souscriptions: readonly {
    readonly id: string
    readonly saison: string
    readonly statut: string
    readonly statutPaiement: string
    readonly montantTotal: number
  }[]
  readonly billetsValides: number
}

/* -------------------------------------------------------------------------- */
/* Sauvegardes — LOT D                                                        */
/* -------------------------------------------------------------------------- */

/**
 * ★ Les champs OPTIONNELS le sont vraiment : le serveur rend `undefined` pour
 * « on ne sait pas » et `JSON.stringify` fait alors disparaître la clé. Les
 * déclarer `| null` ferait croire à une valeur absente là où il n'y a pas de
 * clé du tout, et TypeScript laisserait passer un `null` qui n'arrive jamais.
 * Les colonnes de base, elles, sont bien `| null` : la table les porte ainsi.
 */
export type EtatSauvegarde = 'a_jour' | 'en_retard' | 'jamais' | 'echec' | 'en_cours'
export type StatutArchive = 'en_cours' | 'succes' | 'echec'
export type DeclencheurSauvegarde = 'nocturne' | 'manuel'

export interface LigneSauvegarde {
  readonly tenantId: string
  readonly slug: string
  readonly nom: string
  readonly statutTenant: StatutTenant
  readonly etat: EtatSauvegarde
  readonly derniereReussiteLe?: string
  readonly derniereTentativeLe?: string
  readonly derniereErreur?: string
  readonly octets?: number
  readonly cheminDistant?: string
  readonly heuresDepuisReussite?: number
}

/** Une tentative, telle que la table `sauvegarde` la porte. */
export interface Archive {
  readonly id: string
  readonly tenantSlug: string
  readonly declencheur: DeclencheurSauvegarde
  readonly auteurEmail: string | null
  readonly statut: StatutArchive
  readonly jour: string
  readonly demarreLe: string
  readonly termineLe: string | null
  readonly dureeMs: number | null
  readonly cheminDistant: string | null
  readonly dumpNom: string | null
  readonly dumpOctets: number | null
  readonly fichiersNom: string | null
  readonly fichiersOctets: number | null
  readonly fichiersNombre: number | null
  readonly chiffre: boolean
  readonly cleEmpreinte: string | null
  readonly octetsTotal: number | null
  readonly erreur: string | null
  readonly purgeeLe: string | null
  readonly fournisseur: 'sftp' | 'ilygo_backup'
}

export interface ResumeSauvegardes {
  readonly theatres: number
  readonly aJour: number
  readonly enRetard: number
  readonly jamais: number
  readonly enEchec: number
  readonly octetsConserves: number
  readonly archivesConservees: number
}

export interface EcranSauvegardes {
  readonly resume: ResumeSauvegardes
  readonly theatres: readonly LigneSauvegarde[]
  /** Les tentatives restées `en_cours` depuis plus de six heures. */
  readonly bloquees: readonly Archive[]
}

/**
 * Les réglages, EXPURGÉS par le serveur (`reglagesPourJournal`). Ni mot de
 * passe, ni clé privée, ni clé de chiffrement — seulement son empreinte.
 */
export interface ReglagesSauvegarde {
  readonly actif: boolean
  readonly heureNocturne: number
  readonly tentativesMax: number
  readonly sftp: {
    readonly hote: string
    readonly port: number
    readonly utilisateur: string
    readonly racine: string
    readonly authentification: 'cle' | 'mot-de-passe'
    readonly empreinteHoteVerifiee: boolean
  } | null
  readonly ilygoBackup?: {
    readonly endpoint: string
    readonly bucket: string
    readonly sourcesConfigurees: number
  } | null
  readonly chiffrement: { readonly actif: boolean; readonly cleEmpreinte: string | null }
  readonly retention: { readonly jours: number; readonly semaines: number; readonly mois: number }
}

export interface EcranReglagesSauvegarde {
  readonly reglages: ReglagesSauvegarde
  readonly sftp: { readonly joignable: boolean; readonly detail: string }
  /** Absent sur une ancienne instance pendant un déploiement progressif. */
  readonly destination?: { readonly joignable: boolean; readonly detail: string }
}

export interface InventaireTheatre {
  readonly slug: string
  readonly archives: number
  readonly octets: number
  readonly plusAncienJour?: string
  readonly plusRecentJour?: string
  readonly horsPolitique: number
  readonly octetsHorsPolitique: number
  readonly purgees: number
}

export interface InventaireArchives {
  readonly politique: { readonly jours: number; readonly semaines: number; readonly mois: number }
  readonly total: {
    readonly theatres: number
    readonly archives: number
    readonly octets: number
    readonly horsPolitique: number
    readonly octetsHorsPolitique: number
    readonly purgees: number
  }
  readonly parTheatre: readonly InventaireTheatre[]
}

export interface RapportSauvegarde {
  readonly id: string
  readonly slug: string
  readonly reussie: boolean
  readonly cheminDistant: string
  readonly dureeMs: number
  readonly dumpOctets: number
  readonly fichiersOctets: number
  readonly fichiersNombre: number
  readonly chiffre: boolean
  readonly erreur?: string
}

export interface RapportVerification {
  readonly sauvegardeId: string
  readonly slug: string
  readonly cheminDistant: string
  readonly dumpOctetsDecompresses: number
  readonly dumpInstructions: number | null
  readonly fichiersObjets: number
  readonly chiffre: boolean
  readonly fournisseur?: 'sftp' | 'ilygo_backup'
  readonly morceauxVerifies?: number
}

/** Pourquoi la sauvegarde de sécurité a été prise, ou ne l’a pas été. */
export type MotifFilet = 'pris' | 'base_absente' | 'ecarte' | 'essai'

export interface RapportRestauration {
  readonly sauvegardeId: string
  readonly slug: string
  readonly baseCible: string
  readonly dureeMs: number
  readonly objetsRestaures: number
  readonly essai: boolean
  /** ★ Le filet : la sauvegarde prise juste avant d’écraser, s’il y en a une. */
  readonly filetSauvegardeId?: string
  readonly filetCheminDistant?: string
  readonly filetMotif: MotifFilet
  readonly baseRecreee: boolean
  readonly statutAvant: StatutTenant
  readonly statutApres: StatutTenant
}

/* -------------------------------------------------------------------------- */
/* Clés de cache                                                              */
/* -------------------------------------------------------------------------- */

export const CLES = {
  session: ['console', 'moi'] as const,
  resume: ['console', 'resume'] as const,
  tenants: (filtre: string) => ['console', 'tenants', filtre] as const,
  tenant: (slug: string) => ['console', 'tenant', slug] as const,
  sante: ['console', 'sante'] as const,
  statistiques: ['console', 'statistiques'] as const,
  journal: (filtre: string) => ['console', 'journal', filtre] as const,
  bascule: ['console', 'bascule'] as const,
  apercu: ['console', 'bascule', 'apercu'] as const,
  journalTheatre: (filtre: string) => ['console', 'bascule', 'journal', filtre] as const,
  recherche: (q: string) => ['console', 'bascule', 'personnes', q] as const,
  dossier: (id: string) => ['console', 'bascule', 'dossier', id] as const,
  superadmins: ['console', 'superadmins'] as const,
  /* ★ Quatre clés SŒURS, pas une clé et ses enfants : l'écran des réglages
     SONDE réellement le serveur SFTP (il y écrit un répertoire témoin puis le
     retire). Sous une clé parente commune, chaque sauvegarde lancée à la main
     déclencherait une sonde de plus — jusqu'à vingt secondes d'attente pour
     rafraîchir une information qui n'a pas bougé. */
  sauvegardesTableau: ['console', 'sauvegardes', 'tableau'] as const,
  sauvegardesReglages: ['console', 'sauvegardes', 'reglages'] as const,
  sauvegardesRetention: ['console', 'sauvegardes', 'retention'] as const,
  sauvegardesTenant: (slug: string) => ['console', 'sauvegardes', 'tenant', slug] as const,
} as const

/* -------------------------------------------------------------------------- */
/* Session                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * `null` plutôt qu'une erreur quand personne n'est connecté — même contrat que
 * `GET /auth/moi` côté théâtre : « pas de session » est une réponse normale.
 */
async function chargerSession(): Promise<SessionConsole | null> {
  try {
    return await api.get<SessionConsole>('/console/auth/moi')
  } catch (erreur) {
    if (erreur instanceof ApiError && (erreur.estNonAuthentifie || erreur.estInterdit)) return null
    throw erreur
  }
}

export function useSessionConsole(): UseQueryResult<SessionConsole | null> {
  return useQuery({
    queryKey: CLES.session,
    queryFn: chargerSession,
    retry: false,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  })
}

export function useConnexion() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (identifiants: { email: string; motDePasse: string; code?: string }) =>
      api.post<{ superadmin: SessionConsole['superadmin'] }>(
        '/console/auth/connexion',
        identifiants,
      ),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: CLES.session })
    },
  })
}

export function useDeconnexion() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async () => api.post<{ ok: true }>('/console/auth/deconnexion'),
    onSuccess: () => {
      /* Tout le cache part : les données d'un client consulté en bascule ne
         doivent pas rester lisibles pour la personne suivante sur ce poste. */
      client.clear()
    },
  })
}

/* -------------------------------------------------------------------------- */
/* Lectures                                                                   */
/* -------------------------------------------------------------------------- */

export interface FiltreTenants {
  readonly statut?: StatutTenant | ''
  readonly type?: TypeTenant | ''
  readonly recherche?: string
  readonly inclurePurges?: boolean
}

function query(filtre: FiltreTenants): Record<string, string> {
  const params: Record<string, string> = {}
  if (filtre.statut !== undefined && filtre.statut !== '') params['statut'] = filtre.statut
  if (filtre.type !== undefined && filtre.type !== '') params['type'] = filtre.type
  if (filtre.recherche !== undefined && filtre.recherche !== '') {
    params['recherche'] = filtre.recherche
  }
  if (filtre.inclurePurges === true) params['inclurePurges'] = 'true'
  return params
}

export function useTenants(filtre: FiltreTenants) {
  const params = query(filtre)
  return useQuery({
    queryKey: CLES.tenants(JSON.stringify(params)),
    queryFn: async () =>
      (await api.get<{ tenants: LigneTenant[] }>('/console/tenants', { query: params })).tenants,
  })
}

export function useTenant(slug: string) {
  return useQuery({
    queryKey: CLES.tenant(slug),
    queryFn: async () => api.get<FicheTenant>(`/console/tenants/${slug}`),
  })
}

export function useResume() {
  return useQuery({
    queryKey: CLES.resume,
    queryFn: async () =>
      api.get<{
        tenants: number
        parStatut: Record<StatutTenant, number>
        parSante: Record<StatutSante, number>
        expirentBientot: number
        basculesOuvertes: number
      }>('/console/resume'),
  })
}

export function useSante() {
  return useQuery({
    queryKey: CLES.sante,
    queryFn: async () => api.get<EcranSante>('/console/sante'),
  })
}

export function useStatistiques() {
  return useQuery({
    queryKey: CLES.statistiques,
    queryFn: async () => api.get<Statistiques>('/console/statistiques'),
    /* Un aller-retour par base de tenant : on ne le refait pas à chaque
       montage d'écran. */
    staleTime: 60_000,
  })
}

export function useJournalPlateforme(filtre: {
  action?: string
  tenantSlug?: string
  debut?: string
  fin?: string
}) {
  const params: Record<string, string> = {}
  if (filtre.debut) params['debut'] = filtre.debut
  if (filtre.fin) params['fin'] = filtre.fin
  if (filtre.action !== undefined && filtre.action !== '') params['action'] = filtre.action
  if (filtre.tenantSlug !== undefined && filtre.tenantSlug !== '') {
    params['tenantSlug'] = filtre.tenantSlug
  }
  return useQuery({
    queryKey: CLES.journal(JSON.stringify(params)),
    queryFn: async () =>
      (await api.get<{ entrees: EntreeJournalPlateforme[] }>('/console/journal', { query: params }))
        .entrees,
  })
}

/* -------------------------------------------------------------------------- */
/* Gestes                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Après un geste, on invalide TOUT ce qui décrit le parc — pas seulement la
 * fiche touchée. Une suspension change la liste, les compteurs du bandeau, les
 * statistiques et l'écran de santé ; ne rafraîchir que la fiche laisserait
 * quatre écrans affirmer le contraire de ce que l'on vient de faire.
 */
function useGeste<TVars>(
  appel: (vars: TVars) => Promise<unknown>,
  slugDe: (vars: TVars) => string,
) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: appel,
    onSuccess: async (_donnees, vars) => {
      await Promise.all([
        client.invalidateQueries({ queryKey: CLES.tenant(slugDe(vars)) }),
        client.invalidateQueries({ queryKey: ['console', 'tenants'] }),
        client.invalidateQueries({ queryKey: CLES.resume }),
        client.invalidateQueries({ queryKey: CLES.sante }),
        client.invalidateQueries({ queryKey: CLES.statistiques }),
        client.invalidateQueries({ queryKey: ['console', 'journal'] }),
      ])
    },
  })
}

export function useSuspendre() {
  return useGeste(
    async (vars: { slug: string; motif: string }) =>
      api.post(`/console/tenants/${vars.slug}/suspendre`, { motif: vars.motif }),
    (vars) => vars.slug,
  )
}

export function useReactiver() {
  return useGeste(
    async (vars: { slug: string }) => api.post(`/console/tenants/${vars.slug}/reactiver`, {}),
    (vars) => vars.slug,
  )
}

export function useProlonger() {
  return useGeste(
    async (vars: { slug: string; jours: number }) =>
      api.post(`/console/tenants/${vars.slug}/prolonger`, { jours: vars.jours }),
    (vars) => vars.slug,
  )
}

export function useDeprovisionner() {
  return useGeste(
    async (vars: { slug: string; motif: string }) =>
      api.post(`/console/tenants/${vars.slug}/deprovisionner`, { motif: vars.motif }),
    (vars) => vars.slug,
  )
}

export function useSupprimerBase() {
  return useGeste(
    async (vars: { slug: string; confirmation: string; ignorerDelai?: boolean }) =>
      api.post(`/console/tenants/${vars.slug}/supprimer`, {
        confirmation: vars.confirmation,
        ...(vars.ignorerDelai === true ? { ignorerDelai: true } : {}),
      }),
    (vars) => vars.slug,
  )
}

/**
 * ★★ LOT B-4 — la sortie d'un tenant mort en plein montage (statut encore
 * `provisionnement`). Voir `provisionnement.ts::abandonnerProvisionnement`.
 */
export function useAbandonnerProvisionnement() {
  return useGeste(
    async (vars: { slug: string; confirmation: string }) =>
      api.post(`/console/tenants/${vars.slug}/abandonner-provisionnement`, {
        confirmation: vars.confirmation,
      }),
    (vars) => vars.slug,
  )
}

export interface CreationTenant {
  readonly slug: string
  readonly nom: string
  readonly type: TypeTenant
  readonly notes?: string
  readonly compteAdmin: {
    readonly email: string
    readonly nom: string
    readonly motDePasse: string
  }
  /** EVT-02 — les quatre valeurs d'une soirée, exigées pour `evenement`. */
  readonly evenement?: {
    /** ISO 8601 avec décalage : c'est une heure de spectacle, pas une date. */
    readonly date: string
    readonly lieuNom: string
    readonly jauge: number
    /** Centimes entiers (ARCHITECTURE §3). */
    readonly tarifCentimes: number
  }
}

export interface ResultatCreation {
  readonly tenant: LigneTenant
  readonly etapes: readonly {
    readonly etape: string
    readonly resultat: string
    readonly dureeMs: number
    readonly detail?: string
  }[]
  readonly reprise: boolean
}

export function useCreerTenant() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (corps: CreationTenant) =>
      api.post<ResultatCreation>('/console/tenants', corps),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ['console', 'tenants'] }),
        client.invalidateQueries({ queryKey: CLES.resume }),
        client.invalidateQueries({ queryKey: CLES.sante }),
      ])
    },
  })
}

/* -------------------------------------------------------------------------- */
/* Bascule                                                                    */
/* -------------------------------------------------------------------------- */

export function useBascule() {
  return useQuery({
    queryKey: CLES.bascule,
    queryFn: async () => (await api.get<{ bascule: Bascule | null }>('/console/bascule')).bascule,
    /* Le bandeau doit dire la vérité : une bascule expire au bout de 30 min et
       le serveur la referme de lui-même. On revient donc voir régulièrement,
       plutôt que d'afficher une mention périmée. */
    refetchInterval: 30_000,
    staleTime: 0,
  })
}

function useInvalidationBascule() {
  const client = useQueryClient()
  return async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: CLES.bascule }),
      client.invalidateQueries({ queryKey: CLES.session }),
    ])
    /* Les données du théâtre consulté ne survivent pas à une sortie de
       bascule : elles resteraient à l'écran sans le bandeau qui les explique. */
    client.removeQueries({ queryKey: ['console', 'bascule', 'apercu'] })
    client.removeQueries({ queryKey: ['console', 'bascule', 'journal'] })
    client.removeQueries({ queryKey: ['console', 'bascule', 'personnes'] })
    client.removeQueries({ queryKey: ['console', 'bascule', 'dossier'] })
  }
}

export function useOuvrirBascule() {
  const rafraichir = useInvalidationBascule()
  return useMutation({
    mutationFn: async (vars: { slug: string; motif: string }) =>
      api.post<{ bascule: Bascule }>(`/console/tenants/${vars.slug}/bascule`, {
        motif: vars.motif,
      }),
    onSuccess: rafraichir,
  })
}

export function useFermerBascule() {
  const rafraichir = useInvalidationBascule()
  return useMutation({
    mutationFn: async () => api.del<{ ferme: boolean }>('/console/bascule'),
    onSuccess: rafraichir,
  })
}

export function useApercuTheatre(actif: boolean) {
  return useQuery({
    queryKey: CLES.apercu,
    queryFn: async () =>
      (await api.get<{ apercu: ApercuTheatre }>('/console/bascule/apercu')).apercu,
    enabled: actif,
  })
}

export function useJournalTheatre(actif: boolean, filtre: { journal?: string; action?: string }) {
  const params: Record<string, string> = {}
  if (filtre.journal !== undefined && filtre.journal !== '') params['journal'] = filtre.journal
  if (filtre.action !== undefined && filtre.action !== '') params['action'] = filtre.action
  return useQuery({
    queryKey: CLES.journalTheatre(JSON.stringify(params)),
    queryFn: async () =>
      (
        await api.get<{ entrees: EntreeJournalTheatre[] }>('/console/bascule/journal', {
          query: params,
        })
      ).entrees,
    enabled: actif,
  })
}

export function useRecherchePersonnes(actif: boolean, terme: string) {
  return useQuery({
    queryKey: CLES.recherche(terme),
    queryFn: async () =>
      (
        await api.get<{ resultats: ResultatRecherche[] }>('/console/bascule/personnes', {
          query: { q: terme },
        })
      ).resultats,
    enabled: actif && terme.trim().length >= 2,
  })
}

/**
 * Le dossier d'une personne.
 *
 * ★ `staleTime: Infinity` et aucun `refetch` automatique — délibéré. Chaque
 *   lecture de cette route écrit une ligne « accès aux données personnelles »
 *   dans le journal du THÉÂTRE. Un rafraîchissement au retour de focus
 *   inscrirait donc, dans le journal d'un client, des consultations que
 *   personne n'a demandées ; le jour où il lira son journal, il y verra dix
 *   accès là où il y en a eu un.
 */
export function useDossierPersonne(actif: boolean, id: string | null) {
  return useQuery({
    queryKey: CLES.dossier(id ?? ''),
    queryFn: async () =>
      (await api.get<{ dossier: DossierPersonne }>(`/console/bascule/personnes/${id ?? ''}`))
        .dossier,
    enabled: actif && id !== null,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })
}

/* -------------------------------------------------------------------------- */
/* T-34 — les comptes de console eux-mêmes                                   */
/* -------------------------------------------------------------------------- */

export interface LigneSuperadmin {
  readonly id: string
  readonly email: string
  readonly nom: string
  readonly actif: boolean
  readonly creeLe: string
  readonly derniereConnexionLe: string | null
}

export interface CreationSuperadmin {
  readonly email: string
  readonly nom: string
  /** Absent = engendré par le serveur, rendu une seule fois dans la réponse. */
  readonly motDePasse?: string
}

export interface ResultatCreationSuperadmin {
  readonly superadmin: LigneSuperadmin
  readonly motDePasse?: string
}

export function useSuperadmins() {
  return useQuery({
    queryKey: CLES.superadmins,
    queryFn: async () =>
      (await api.get<{ superadmins: LigneSuperadmin[] }>('/console/superadmins')).superadmins,
  })
}

export function useCreerSuperadmin() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (corps: CreationSuperadmin) =>
      api.post<ResultatCreationSuperadmin>('/console/superadmins', corps),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: CLES.superadmins })
    },
  })
}

function useGesteSuperadmin() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { id: string; geste: 'retirer' | 'reactiver' }) =>
      api.post<{ superadmin: LigneSuperadmin }>(`/console/superadmins/${vars.id}/${vars.geste}`),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: CLES.superadmins })
    },
  })
}

export function useRetirerSuperadmin() {
  const geste = useGesteSuperadmin()
  return {
    ...geste,
    mutateAsync: (vars: { id: string }) => geste.mutateAsync({ id: vars.id, geste: 'retirer' }),
  }
}

export function useReactiverSuperadmin() {
  const geste = useGesteSuperadmin()
  return {
    ...geste,
    mutateAsync: (vars: { id: string }) => geste.mutateAsync({ id: vars.id, geste: 'reactiver' }),
  }
}

/* -------------------------------------------------------------------------- */
/* Sauvegardes — LOT D                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Le tableau de bord des sauvegardes.
 *
 * ★ `refetchInterval` de 30 s, et c'est un choix, pas un réflexe : cet écran
 * reste ouvert pendant qu'une campagne tourne. Sans rafraîchissement, il
 * afficherait « en cours » sur un théâtre déjà sauvegardé depuis dix minutes —
 * un écran de supervision qui ment sur le présent n'est pas un écran de
 * supervision. La lecture est purement locale (base de plateforme), donc son
 * coût est celui d'une requête indexée.
 */
export function useSauvegardes() {
  return useQuery({
    queryKey: CLES.sauvegardesTableau,
    queryFn: async () => api.get<EcranSauvegardes>('/console/sauvegardes'),
    refetchInterval: 30_000,
  })
}

/**
 * Les réglages et la sonde du serveur distant.
 *
 * ★ La sonde ÉCRIT réellement un répertoire témoin sur le SFTP puis le retire :
 * c'est ce qui la rend fiable — un compte qui se connecte sans pouvoir écrire
 * est le mode de panne le plus fréquent, et une sonde qui se contenterait de se
 * connecter le déclarerait vert. Elle part donc à l'OUVERTURE de l'écran (une
 * destination injoignable qu'il faudrait penser à demander n'est pas
 * surveillée), mais son résultat vaut une minute et l'écran offre un bouton
 * pour la rejouer. `enabled` reste piloté par l'appelant pour qu'un écran qui
 * n'aurait pas besoin de la sonde puisse s'en passer.
 */
export function useReglagesSauvegarde(actif: boolean) {
  return useQuery({
    queryKey: CLES.sauvegardesReglages,
    queryFn: async () => api.get<EcranReglagesSauvegarde>('/console/sauvegardes/reglages'),
    enabled: actif,
    staleTime: 60_000,
    retry: false,
  })
}

/** Ce que pèse l'historique, et ce que la rétention aurait dû purger. */
export function useInventaireArchives() {
  return useQuery({
    queryKey: CLES.sauvegardesRetention,
    queryFn: async () => api.get<InventaireArchives>('/console/sauvegardes/retention'),
    staleTime: 60_000,
  })
}

/** L'historique complet d'un théâtre — cinquante tentatives au plus. */
export function useSauvegardesTenant(slug: string) {
  return useQuery({
    queryKey: CLES.sauvegardesTenant(slug),
    queryFn: async () =>
      (
        await api.get<{ slug: string; sauvegardes: Archive[] }>(
          `/console/sauvegardes/tenants/${slug}`,
        )
      ).sauvegardes,
  })
}

/**
 * Après un geste, on rafraîchit le tableau, l'inventaire, l'historique du
 * théâtre touché et le journal de la plateforme — mais JAMAIS la sonde SFTP,
 * qui coûte un aller-retour réseau pour une information qui n'a pas bougé.
 */
function useGesteSauvegarde<TVars, TResultat>(
  appel: (vars: TVars) => Promise<TResultat>,
  slugDe: (vars: TVars) => string | undefined,
) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: appel,
    onSuccess: async (_donnees, vars) => {
      const slug = slugDe(vars)
      await Promise.all([
        client.invalidateQueries({ queryKey: CLES.sauvegardesTableau }),
        client.invalidateQueries({ queryKey: CLES.sauvegardesRetention }),
        client.invalidateQueries({ queryKey: ['console', 'journal'] }),
        ...(slug === undefined
          ? []
          : [client.invalidateQueries({ queryKey: CLES.sauvegardesTenant(slug) })]),
      ])
    },
  })
}

/** « Sauvegarder maintenant », pour UN théâtre. Journalisé nominativement. */
export function useLancerSauvegarde() {
  return useGesteSauvegarde(
    async (vars: { slug: string }) =>
      api.post<{ rapport: RapportSauvegarde }>(`/console/sauvegardes/tenants/${vars.slug}`),
    (vars) => vars.slug,
  )
}

/**
 * LA RÉPÉTITION : rouvre l'archive de bout en bout sans rien écrire nulle part.
 *
 * C'est le seul geste de cette console qui permette de répondre « oui » à
 * « avez-vous éprouvé vos restaurations ? » sans casser un théâtre pour le
 * prouver.
 */
export function useVerifierArchive() {
  return useGesteSauvegarde(
    async (vars: { id: string; slug: string }) =>
      api.post<{ verification: RapportVerification }>(`/console/sauvegardes/${vars.id}/verifier`),
    (vars) => vars.slug,
  )
}

/**
 * ★★ LA RESTAURATION — le geste le plus lourd du produit.
 *
 * Elle ÉCRASE les données actuelles du théâtre, y compris les ventes faites
 * depuis la sauvegarde. Le serveur exige le slug recopié dans le corps de la
 * requête ; l'écran l'exige à la saisie, et les deux contrôles sont
 * indépendants — celui du navigateur est un confort, celui du serveur fait foi.
 */
export function useRestaurerArchive() {
  return useGesteSauvegarde(
    async (vars: { id: string; slug: string; confirmation: string; baseCible?: string }) =>
      api.post<{ rapport: RapportRestauration }>(`/console/sauvegardes/${vars.id}/restaurer`, {
        confirmation: vars.confirmation,
        ...(vars.baseCible === undefined ? {} : { baseCible: vars.baseCible }),
      }),
    (vars) => vars.slug,
  )
}

/** Purger une archive à la main. Le serveur refuse la dernière restaurable. */
export function usePurgerArchive() {
  return useGesteSauvegarde(
    async (vars: { id: string; slug: string }) =>
      api.del<{ purgee: true; slug: string; jour: string }>(`/console/sauvegardes/${vars.id}`),
    (vars) => vars.slug,
  )
}
